"use client"

import { useRef, useState, useEffect, useCallback } from "react"
import { createContainer } from "unstated-next"
import { colors } from "@/theme/colors"

export enum Tool {
  Brush = "Brush",
  Eraser = "Eraser",
}

const CanvasContainer = createContainer(() => {
  const [selectedTool, setSelectedTool] = useState<
    typeof Tool.Brush | typeof Tool.Eraser
  >(Tool.Brush)
  const [selectedToolSize, setSelectedToolSize] = useState(50)

  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  const [isMasked, setIsMasked] = useState<boolean>(false)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const prevPoint = useRef<null | { x: number; y: number }>(null)

  const [mouseDown, setMouseDown] = useState(false)
  const [mouseMove, setMouseMove] = useState(false)

  const [mouseUp, setMouseUp] = useState(true)

  const [touchStart, setTouchStart] = useState(false)
  const [touchMove, setTouchMove] = useState(false)

  const [paths, setPaths] = useState<ImageData[]>([])
  const [popped, setPopped] = useState<ImageData[]>([])

  useEffect(() => {
    resetCanvas()
  }, [canvasSize])

  const draw = useCallback(
    ({
      prevPoint,
      currentPoint,
      ctx,
    }: {
      ctx: CanvasRenderingContext2D
      currentPoint: { x: number; y: number }
      prevPoint: { x: number; y: number } | null
    }) => {
      const { x: currX, y: currY } = currentPoint
      const startPoint = prevPoint ?? currentPoint

      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      ctx.lineWidth = selectedToolSize
      ctx.globalCompositeOperation =
        selectedTool === Tool.Eraser ? "destination-out" : "source-over"
      ctx.strokeStyle = colors.primary.DEFAULT
      ctx.beginPath()
      ctx.moveTo(startPoint.x, startPoint.y)
      ctx.lineTo(currX, currY)
      ctx.stroke()
    },
    [selectedTool, selectedToolSize]
  )

  const onMouseDown = () => {
    setMouseDown(true)
    setMouseUp(false)
    if (popped.length) setPopped([])
  }

  const onTouchStart = () => {
    setTouchStart(true)
    if (popped.length) setPopped([])
  }

  const resetTool = () => {
    setSelectedTool(Tool.Brush)
  }

  const resetCanvas = () => {
    setPaths([])
    setPopped([])
  }

  const undo = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const newPaths = [...paths]
    const poppedPoint = newPaths.pop()
    if (!poppedPoint) return

    setPopped([...popped, poppedPoint])
    setPaths(newPaths)

    if (newPaths.length > 0) {
      ctx.putImageData(newPaths[newPaths.length - 1], 0, 0)
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      setPaths([])
      setIsMasked(false)
    }
  }

  const redo = () => {
    if (!popped.length) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const newPopped = [...popped]
    const newPaths = [...paths]
    const poppedPoint = newPopped.pop()

    if (!poppedPoint) return

    newPaths.push(poppedPoint)
    setPaths(newPaths)
    setPopped(newPopped)

    if (newPaths.length > 0) {
      ctx.putImageData(newPaths[newPaths.length - 1], 0, 0)
      setIsMasked(true)
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const mouseMoveHandler = (e: MouseEvent) => {
      if (!mouseDown || mouseUp) return

      const rect = canvas.getBoundingClientRect()
      const currentPoint = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }

      const ctx = canvas.getContext("2d")
      if (!ctx || !currentPoint) return

      draw({ ctx, currentPoint, prevPoint: prevPoint.current })

      if (!mouseMove) setMouseMove(true)
      if (!isMasked) setIsMasked(true)

      prevPoint.current = currentPoint
    }

    const touchMoveHandler = (e: TouchEvent) => {
      if (!touchStart) return

      const rect = canvas.getBoundingClientRect()

      const { clientX, clientY } = e.touches[0]
      const currentPoint = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      }

      const ctx = canvas.getContext("2d")
      if (!ctx || !currentPoint) return

      draw({ ctx, currentPoint, prevPoint: prevPoint.current })

      if (!touchMove) setTouchMove(true)
      if (!isMasked) setIsMasked(true)

      prevPoint.current = currentPoint
    }

    const mouseUpHandler = () => {
      if (mouseUp) return

      setMouseDown(false)
      setMouseUp(true)
      prevPoint.current = null

      const ctx = canvas?.getContext("2d")
      if (!ctx) return

      if (!mouseMove) return

      const canvasData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      setPaths((prev) => [...prev, canvasData])
    }

    const touchEndHandler = () => {
      setTouchStart(false)
      setTouchMove(false)
      prevPoint.current = null

      const ctx = canvas?.getContext("2d")
      if (!ctx) return

      if (!touchMove) return

      const canvasData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      setPaths((prev) => [...prev, canvasData])
    }

    const mouseLeaveHandler = () => {
      if (!mouseDown) return

      setMouseDown(false)
      setMouseUp(true)
      setMouseMove(false)
      prevPoint.current = null

      const ctx = canvas?.getContext("2d")
      if (!ctx) return

      const canvasData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      setPaths((prev) => [...prev, canvasData])
    }

    canvas.addEventListener("mousemove", mouseMoveHandler)
    canvas.addEventListener("mouseleave", mouseLeaveHandler)
    canvas.addEventListener("mouseup", mouseUpHandler)

    canvas.addEventListener("touchmove", touchMoveHandler)
    canvas.addEventListener("touchend", touchEndHandler)

    return () => {
      canvas.removeEventListener("mousemove", mouseMoveHandler)
      canvas.removeEventListener("mouseleave", mouseLeaveHandler)
      canvas.removeEventListener("mouseup", mouseUpHandler)

      canvas.removeEventListener("touchmove", touchMoveHandler)
      canvas.removeEventListener("touchend", touchEndHandler)
    }
  }, [draw, isMasked, mouseDown, mouseUp, touchStart, mouseMove, touchMove])

  return {
    canvasRef,
    onMouseDown,
    onTouchStart,
    resetCanvas,
    resetTool,
    selectedToolSize,
    setSelectedToolSize,
    selectedTool,
    setSelectedTool,
    canvasSize,
    setCanvasSize,
    isMasked,
    setIsMasked,
    undo,
    redo,
    paths,
  }
})

export const CanvasProvider = CanvasContainer.Provider
export const useCanvas = CanvasContainer.useContainer
